import {
  Cake,
  CalendarToday,
  CardGiftcard,
  Check,
  Close,
  EmojiEmotions,
  Person
} from '@mui/icons-material';
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  Paper,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format, isValid } from 'date-fns';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { submitBirthdayViaCollectionLink } from '../services/api.ts';

// Common emojis organized by categories for a better UX
const EMOJI_CATEGORIES = {
  favorites: ['🎂', '🎁', '🎈', '🎉', '🥳', '🎊'],
  faces: ['😀', '😊', '😍', '🤩', '😎', '🥰', '😇'],
  party: ['✨', '🌟', '⭐', '🌈', '🦄', '🍭', '🍬', '💯'],
  gifts: ['💝', '💖', '💐', '🍰', '🧁', '👑', '💎']
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1,
      duration: 0.3
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.3 }
  }
};

const buttonVariants = {
  hover: { scale: 1.05, transition: { duration: 0.2 } },
  tap: { scale: 0.95, transition: { duration: 0.1 } }
};

const CollectForm = () => {
  const { linkCode } = useParams<{ linkCode: string }>();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState<Date | null>(null);
  const [emoji, setEmoji] = useState('');
  const [gifts, setGifts] = useState<string[]>(['']);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Emoji picker state
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<null | HTMLElement>(null);
  const [activeEmojiCategory, setActiveEmojiCategory] = useState<keyof typeof EMOJI_CATEGORIES>('favorites');
  const emojiPickerOpen = Boolean(emojiAnchorEl);
  
  // Basic validation
  const isFormValid = name.trim() !== '' && birthDate !== null && isValid(birthDate);
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!linkCode || !isFormValid) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Filter out empty gift ideas
      const filteredGifts = gifts.filter(gift => gift.trim() !== '');
      
      await submitBirthdayViaCollectionLink(linkCode, {
        name: name.trim(),
        birth_date: format(birthDate!, 'yyyy-MM-dd'),
        emoji: emoji || undefined,
        gifts: filteredGifts.length > 0 ? filteredGifts : undefined
      });
      
      setSuccess(true);
      
      // Reset form
      setName('');
      setBirthDate(null);
      setEmoji('');
      setGifts(['']);
      
    } catch (err) {
      console.error('Error submitting birthday:', err);
      setError('Failed to submit your birthday. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle adding new gift input
  const handleAddGift = () => {
    setGifts([...gifts, '']);
  };
  
  // Handle updating gift input
  const handleGiftChange = (index: number, value: string) => {
    const newGifts = [...gifts];
    newGifts[index] = value;
    setGifts(newGifts);
  };
  
  // Handle removing gift input
  const handleRemoveGift = (index: number) => {
    // Fixed: Now properly removes the gift at the specified index
    const newGifts = [...gifts];
    newGifts.splice(index, 1);
    
    // Always keep at least one gift input
    if (newGifts.length === 0) {
      newGifts.push('');
    }
    
    setGifts(newGifts);
  };
  
  // Auto-add a new gift input when the last one is typed in
  useEffect(() => {
    const lastGift = gifts[gifts.length - 1];
    if (lastGift && lastGift.trim() !== '' && gifts.length < 10) {
      setGifts([...gifts, '']);
    }
  }, [gifts]);
  
  // Handle emoji picker open
  const handleEmojiPickerOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setEmojiAnchorEl(event.currentTarget);
  };
  
  // Handle emoji picker close
  const handleEmojiPickerClose = () => {
    setEmojiAnchorEl(null);
  };
  
  // Handle emoji selection
  const handleEmojiSelect = (selectedEmoji: string) => {
    setEmoji(selectedEmoji);
    handleEmojiPickerClose();
  };

  // Handle emoji category change
  const handleEmojiCategoryChange = (category: keyof typeof EMOJI_CATEGORIES) => {
    setActiveEmojiCategory(category);
  };
  
  // Success view after submitting the form - completely redesigned
  const SuccessView = () => (
    <Box 
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      sx={{
        textAlign: 'center',
        py: { xs: 2, sm: 4 }
      }}
    >
      <motion.div variants={itemVariants}>
        <Avatar
          sx={{
            width: { xs: 60, md: 96 },
            height: { xs: 60, md: 96 },
            backgroundColor: theme.palette.primary.main,
            margin: '0 auto',
            mb: 3
          }}
        >
          <Check sx={{ fontSize: { xs: 36, md: 48 }, color: 'white' }} />
        </Avatar>
      </motion.div>
      
      <motion.div variants={itemVariants}>
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" sx={{ mb: 2 }}>
          Thank You!
        </Typography>
      </motion.div>
      
      
      <Box
        sx={{
          backgroundColor: theme.palette.primary.light,
          borderRadius: 4,
          p: { xs: 2, sm: 4 },
          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
          mt: 3,
          mb: 1
        }}
        component={motion.div}
        variants={itemVariants}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <img 
            src="/apple-icon-180x180.png" 
            alt="Birthday Planner Logo" 
            style={{ 
              width: '6rem', 
              height: '6rem'
            }} 
          />
        </Box>
        
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="white" sx={{ mb: 2 }}>
          Never Miss a Birthday!
        </Typography>
        
        <Typography variant="body1" color="white" sx={{ mb: 3, opacity: 0.9 }}>
          Keep track of birthdays with reminders and gift ideas.
        </Typography>
        
        <motion.div
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
        >
          <Button 
            variant="contained" 
            size="large"
            component={RouterLink}
            to="/app"
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: isMobile ? 1 : 1.5,
              backgroundColor: 'white',
              color: theme.palette.primary.main,
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: 'white',
                opacity: 0.9
              }
            }}
          >
            Get Started Now 🚀
          </Button>
        </motion.div>
      </Box>
    </Box>
  );
  
  // Hide floating feedback button
  useEffect(() => {
    // Add a specific class to the body to indicate we're on the collect page
    document.body.classList.add('collect-page');
    
    // Remove it when component unmounts
    return () => {
      document.body.classList.remove('collect-page');
    };
  }, []);
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box 
        sx={{ 
          minHeight: '100vh',
          background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
          py: { xs: 2, sm: 6 },
          px: { xs: 1, sm: 2 },
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Decorative background elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            right: '5%',
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha('#fff', 0.1)} 0%, ${alpha('#fff', 0)} 70%)`,
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '15%',
            left: '10%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha('#fff', 0.08)} 0%, ${alpha('#fff', 0)} 70%)`,
            zIndex: 0
          }}
        />
        
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Paper
            elevation={6}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: '0 16px 40px rgba(0, 0, 0, 0.15)',
              transition: 'box-shadow 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0 20px 48px rgba(0, 0, 0, 0.2)'
              }
            }}
          >
            {success ? (
              <Box sx={{ p: { xs: 2, sm: 4 } }}>
                <SuccessView />
              </Box>
            ) : (
              <>
                {/* Header */}
                <Box 
                  sx={{ 
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    color: 'white',
                    p: { xs: 2.5, sm: 4 },
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -20,
                      right: -20,
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -30,
                      left: -30,
                      width: 150,
                      height: 150,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }}
                  />
                  
                  <img 
                    src="/apple-icon-180x180.png" 
                    alt="Birthday Planner Logo" 
                    style={{ 
                      width: '5rem', 
                      height: '5rem'
                    }} 
                  />
                  <Typography variant="h4" fontWeight="bold">
                    Share Your Birthday
                  </Typography>
                </Box>
                
                {/* Content */}
                <Box sx={{ p: { xs: 2, sm: 4 } }}>
                  <Box 
                    component={motion.form}
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    onSubmit={handleSubmit}
                  >
                    {/* Name Field */}
                    <Box 
                      component={motion.div}
                      variants={itemVariants}
                      sx={{ mb: { xs: 2, sm: 3 } }}
                    >
                      <Typography variant="subtitle2" color="primary" fontWeight="medium" sx={{ mb: 0.5, ml: 0.5 }}>
                        Your Name
                      </Typography>
                      <TextField
                        placeholder="Enter your full name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        required
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person color="primary" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                      />
                    </Box>
                    
                    {/* Birth Date Field */}
                    <Box 
                      component={motion.div}
                      variants={itemVariants}
                      sx={{ mb: { xs: 2, sm: 3 } }}
                    >
                      <Typography variant="subtitle2" color="primary" fontWeight="medium" sx={{ mb: 0.5, ml: 0.5 }}>
                        Birth Date
                      </Typography>
                      <Box sx={{ position: 'relative' }}>
                        <DatePicker
                          value={birthDate}
                          onChange={(newValue) => setBirthDate(newValue)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              placeholder: "Select your birth date",
                              InputProps: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <IconButton
                                      onClick={(e) => {
                                        // Find and click the calendar button
                                        const calendarBtn = e.currentTarget.closest('.MuiInputBase-root')?.querySelector('.MuiInputAdornment-end button');
                                        if (calendarBtn) {
                                          (calendarBtn as HTMLElement).click();
                                        }
                                      }}
                                      color="primary"
                                      sx={{ p: 0.5 }}
                                    >
                                      <CalendarToday fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              },
                              sx: { 
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                },
                                // Hide the default calendar button
                                '& .MuiInputAdornment-end': {
                                  opacity: 0,
                                  width: 0,
                                  marginLeft: 0,
                                  overflow: 'hidden',
                                  position: 'absolute'
                                }
                              }
                            }
                          }}
                          format="dd/MM/yyyy"
                          views={['day', 'month', 'year']}
                          openTo="day"
                        />
                      </Box>
                    </Box>
                    
                    {/* Emoji Field */}
                    <Box 
                      component={motion.div}
                      variants={itemVariants}
                      sx={{ mb: { xs: 2, sm: 3 } }}
                    >
                      <Typography variant="subtitle2" color="primary" fontWeight="medium" sx={{ mb: 0.5, ml: 0.5 }}>
                        Your Emoji
                      </Typography>
                      <TextField
                        placeholder="An emoji that represents you"
                        value={emoji}
                        onChange={(e) => setEmoji(e.target.value)}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Button
                                onClick={handleEmojiPickerOpen}
                                variant="text"
                                color="primary"
                                sx={{ minWidth: 'auto', p: 0.5 }}
                              >
                                <EmojiEmotions />
                              </Button>
                            </InputAdornment>
                          )
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                      />
                      
                      <Menu
                        anchorEl={emojiAnchorEl}
                        open={emojiPickerOpen}
                        onClose={handleEmojiPickerClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        PaperProps={{
                          sx: { 
                            p: 2,
                            maxWidth: '320px',
                            maxHeight: '400px',
                            borderRadius: 2,
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
                          }
                        }}
                      >
                        {/* Category Tabs */}
                        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {Object.keys(EMOJI_CATEGORIES).map((category) => (
                            <Button
                              key={category}
                              variant={activeEmojiCategory === category ? "contained" : "outlined"}
                              size="small"
                              onClick={() => handleEmojiCategoryChange(category as keyof typeof EMOJI_CATEGORIES)}
                              sx={{ 
                                minWidth: 'auto',
                                px: 1.5,
                                borderRadius: 4,
                                textTransform: 'capitalize'
                              }}
                            >
                              {category}
                            </Button>
                          ))}
                        </Box>
                        
                        {/* Emojis Grid */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                          {EMOJI_CATEGORIES[activeEmojiCategory].map((emojiOption, index) => (
                            <Button
                              key={index}
                              onClick={() => handleEmojiSelect(emojiOption)}
                              sx={{ 
                                p: 1, 
                                minWidth: 'auto',
                                fontSize: '1.5rem',
                                borderRadius: 2,
                                '&:hover': {
                                  backgroundColor: theme.palette.action.hover
                                }
                              }}
                            >
                              {emojiOption}
                            </Button>
                          ))}
                        </Box>
                      </Menu>
                    </Box>
                    
                    {/* Gift Ideas */}
                    <Box 
                      component={motion.div}
                      variants={itemVariants}
                      sx={{ mb: { xs: 2, sm: 4 } }}
                    >
                      <Typography variant="subtitle2" color="primary" fontWeight="medium" sx={{ mb: 0.5, ml: 0.5 }}>
                        Gift Ideas (Optional)
                      </Typography>
                      {!isMobile && (
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, ml: 0.5, display: 'block' }}>
                          Help your friends know what you'd like
                        </Typography>
                      )}
                      
                      {gifts.map((gift, index) => (
                        <Box 
                          key={index} 
                          sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1
                          }}
                        >
                          <TextField
                            value={gift}
                            onChange={(e) => handleGiftChange(index, e.target.value)}
                            placeholder="Gift idea"
                            fullWidth
                            variant="outlined"
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CardGiftcard color="primary" />
                                </InputAdornment>
                              )
                            }}
                            sx={{ 
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                              }
                            }}
                          />
                          
                          <IconButton 
                            onClick={() => handleRemoveGift(index)}
                            disabled={gifts.length === 1 && gifts[0] === ''}
                            sx={{ ml: 1 }}
                            color="error"
                            size="small"
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                    
                    {!isMobile && <Divider sx={{ my: 2, opacity: 0.6 }} />}
                    
                    {/* Submit Button */}
                    <Box 
                      component={motion.div}
                      variants={itemVariants}
                      sx={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        mt: { xs: 1, sm: 2 }
                      }}
                    >
                      <motion.div
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap="tap"
                        style={{ width: isMobile ? '100%' : 'auto' }}
                      >
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={loading || !isFormValid}
                          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Cake />}
                          fullWidth={isMobile}
                          sx={{ 
                            borderRadius: 2,
                            py: isMobile ? 1.2 : 1.5,
                            px: 4,
                            fontSize: isMobile ? '0.9rem' : '1rem',
                            fontWeight: 'bold',
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                            '&:hover': {
                              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
                            },
                            '&.Mui-disabled': {
                              color: 'rgba(255, 255, 255, 0.6)',
                              background: alpha(theme.palette.primary.main, 0.7)
                            }
                          }}
                        >
                          {loading ? 'Submitting...' : 'Submit Birthday'}
                        </Button>
                      </motion.div>
                    </Box>
                    
                    {error && (
                      <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
                        {error}
                      </Alert>
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Paper>
        </Container>
      </Box>
      
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" sx={{ width: '100%', borderRadius: 2 }}>
          {error}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default CollectForm; 